import { useRxCollection } from '@/providers/rxdb/rx-hooks';
import { type Dispatch, type SetStateAction, useEffect, useMemo, useRef } from 'react';

export const useFilterTagCleanup = <T extends TagFilterState>(params: UseFilterTagCleanupParams<T>) => {
  const { value, onChange, setDraftValue, draftValue } = params;

  const valueRef = useRef(value);
  valueRef.current = value;

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const collection = useRxCollection('tags');

  const watchedTagIds = useMemo(
    () => [
      ...new Set([...value.includedTagIds, ...value.excludedTagIds, ...draftValue.includedTagIds, ...draftValue.excludedTagIds]),
    ],
    [value.includedTagIds, value.excludedTagIds, draftValue.includedTagIds, draftValue.excludedTagIds]
  );

  useEffect(() => {
    if (!collection || !watchedTagIds.length) return;

    const sub = collection.findByIds(watchedTagIds).$.subscribe((result) => {
      const existingIds = new Set(result.keys());

      setDraftValue((prev) => {
        const nextIncluded = prev.includedTagIds.filter((id) => existingIds.has(id));
        const nextExcluded = prev.excludedTagIds.filter((id) => existingIds.has(id));

        const hasDraftChanges =
          nextIncluded.length !== prev.includedTagIds.length || nextExcluded.length !== prev.excludedTagIds.length;

        return hasDraftChanges ? { ...prev, includedTagIds: nextIncluded, excludedTagIds: nextExcluded } : prev;
      });

      const current = valueRef.current;
      const nextIncluded = current.includedTagIds.filter((id) => existingIds.has(id));
      const nextExcluded = current.excludedTagIds.filter((id) => existingIds.has(id));

      const hasAppliedChanges =
        nextIncluded.length !== current.includedTagIds.length || nextExcluded.length !== current.excludedTagIds.length;

      if (!hasAppliedChanges) return;

      onChangeRef.current({ ...current, includedTagIds: nextIncluded, excludedTagIds: nextExcluded });
    });

    return () => sub.unsubscribe();
  }, [collection, watchedTagIds, setDraftValue]);
};

type TagFilterState = {
  includedTagIds: string[];
  excludedTagIds: string[];
};

interface UseFilterTagCleanupParams<T extends TagFilterState> {
  value: T;
  draftValue: T;
  onChange: (value: T) => void;
  setDraftValue: Dispatch<SetStateAction<T>>;
}
